import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useInvoices} from '../hooks/useInvoices';
import {useInvoicesFilter} from '../hooks/useInvoicesFilter';
import {useInvoicesSearch} from '../hooks/useInvoicesSearch';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {Invoice} from '../types';
import {UseInvoicesSelectionResult} from '../hooks/useInvoicesSelection';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + RowSelectionDataCell + the app-local PaginationControls) to the
// shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows and supported
// selection — but it structurally could not have sorting, token pagination, or
// the loading/empty/error states, because all three come from the shared
// component. Five checklist gaps close by changing this one component (§3z).
//
// ⚠️ NOTE FOR THE REMAINING MODULES: `src/components/PaginationControls` and
// `src/hooks/usePagination` are NOT deleted. Other modules still import them;
// they come out in a later wave once every module is migrated.

interface RecordsTableProps {
    /**
     * Narrows the list to one customer, SERVER-side. The customer detail page
     * embeds this table in its Invoices tab and passes the customer's id; the
     * index page passes nothing. It is part of the query key, so switching
     * customers refetches rather than serving the previous one's cache.
     */
    customerId?: string;
    /**
     * Selection is read by IndexPage and passed down, because the delete and
     * send buttons live in the page HEADER — siblings, not children of this
     * table. The state itself is the Redux invoices.selection slice (FNP-951);
     * taking it as a prop keeps this component store-free and testable, and
     * lets the embedded customer-tab instance render with no selection at all.
     */
    selection?: UseInvoicesSelectionResult;
}

export const RecordsTable = ({
                                 customerId,
                                 selection,
                             }: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions();
    const navigate = useNavigate();

    const {filters} = useInvoicesFilter();

    const {
        columns,
        // The loaded window BEFORE filtering — the denominator the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useInvoices(customerId, filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useInvoicesSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The select column is HIDDEN, not disabled, when the action is not
    // permitted (§2, §10). Gated on the same action the delete button is gated
    // on — a checkbox with no permitted batch action behind it is an affordance
    // that does nothing.
    const canSelect = !!selection
        && allowedActions.includes(rbacActions.DeleteInvoice);

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection.selection[item.id])
        : undefined;

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both: "no invoices yet" sends an
    // operator hunting for a bug when a search simply matched nothing.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No invoices match this search')
        : I18n.get('No invoices found');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown, which token pagination cannot honour — there is no page
              * to resize and no total to divide (§9). The previous table passed
              * `limit={10}` and a no-op handler to satisfy it, so the dropdown
              * rendered and did nothing.
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load invoices')}
                >
                    {I18n.get(
                        'The billing API did not return the invoice list. Try Reload; '
                        + 'if it persists the API may be unavailable in this environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<Invoice>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the
                // same scope the keyword filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records: Invoice[]) =>
                        selection.handleSelectAllRows(true, records)
                    : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}: {record: Invoice}) =>
                    navigate(`/invoices/${encodeURIComponent(record.id)}`)}
            />

            {/*
              * A SIBLING of the table, not a table prop: the table renders rows,
              * the footer answers "have I seen everything?" (§9).
              *
              * `loadedCount` is the rows in the browser — NEVER a dataset total.
              * `filterTerm` must be passed whenever a keyword is active, or a
              * filtered view silently reports the unfiltered state.
              */}
            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'invoice'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
